/**
 * Pills horizontais sticky das categorias — 6ª ilha.
 *
 * Substitui o `pillsHtml` do V1 `renderHome()`. Fica sticky abaixo do
 * HeaderIsland (top dinâmico via measure do header).
 *
 * Click → scroll suave até `#delivery-cat-${idx}` no ProductCardsIsland.
 *
 * Padrão UX (iFood/Anota AI):
 *  - Scroll horizontal nativo no mobile
 *  - Wheel horizontal no desktop
 *  - Mask gradient nas bordas pra indicar "tem mais"
 */
import { useEffect, useRef, useState } from 'react';
import type { MenuCategory } from '../types';
import { tr } from '../i18n';

interface CategoryPillsIslandProps {
  categories: MenuCategory[];
}

export function CategoryPillsIsland({ categories }: CategoryPillsIslandProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [topOffset, setTopOffset] = useState(0);

  // Mede altura do HeaderIsland pra sticky-top dinâmico — header pode
  // crescer/encolher dependendo de banner/badges.
  useEffect(() => {
    const measure = () => {
      const h = document.getElementById('delivery-v2-header')?.offsetHeight || 0;
      setTopOffset(h);
    };
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    const hdr = document.getElementById('delivery-v2-header');
    if (hdr && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(hdr);
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);

  // Desktop wheel → scroll horizontal
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const visibleCats = categories.filter((c) => c.items && c.items.length > 0);
  if (visibleCats.length === 0) return null;

  const goTo = (idx: number) => {
    const el = document.getElementById(`delivery-cat-${idx}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      ref={wrapRef}
      className="bg-white border-b border-gray-100 shadow-sm overflow-x-auto hide-scroll px-4 py-2 sticky z-20"
      style={{
        top: topOffset,
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)',
        maskImage: 'linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)',
      }}
    >
      <div className="flex gap-2 whitespace-nowrap">
        {visibleCats.map((cat, idx) => (
          <button
            key={cat.category_id}
            type="button"
            onClick={() => goTo(idx)}
            className="text-xs font-bold text-gray-700 bg-white border border-gray-200 hover:border-red-300 hover:text-red-600 active:scale-95 transition px-3 py-1.5 rounded-full shadow-sm shrink-0"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {tr(cat.category_name)}
          </button>
        ))}
      </div>
    </div>
  );
}
